import { useCallback, useState } from 'react';
import {
  getEmailMailboxStatusAdmin,
  getEmailTemplateAdmin,
  listEmailTemplatesAdmin,
  publishEmailTemplateAdmin,
  upsertEmailTemplateAdmin,
  type EmailMailboxStatus,
  type EmailTemplateDetail,
  type EmailTemplateSummary,
  type EmailTemplateVariable,
} from '../services/email/emailTemplatesAdmin';

export function useEmailTemplatesAdmin() {
  const [templates, setTemplates] = useState<EmailTemplateSummary[]>([]);
  const [wired, setWired] = useState<Array<{ runtimeKey: string; alias: string }>>([]);
  const [mailbox, setMailbox] = useState<EmailMailboxStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, status] = await Promise.all([
        listEmailTemplatesAdmin(),
        getEmailMailboxStatusAdmin(),
      ]);
      setTemplates(Array.isArray(list.templates) ? list.templates : []);
      setWired(Array.isArray(list.wired) ? list.wired : []);
      setMailbox(status);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTemplate = useCallback(async (idOrAlias: string) => {
    const { template } = await getEmailTemplateAdmin(idOrAlias);
    return template;
  }, []);

  const saveTemplate = useCallback(async (input: {
    idOrAlias?: string;
    name: string;
    subject: string;
    html: string;
    alias?: string;
    variables?: EmailTemplateVariable[];
    publish?: boolean;
  }) => {
    const result = await upsertEmailTemplateAdmin(input);
    await refresh();
    return result;
  }, [refresh]);

  const publishTemplate = useCallback(async (idOrAlias: string) => {
    const result = await publishEmailTemplateAdmin(idOrAlias);
    await refresh();
    return result;
  }, [refresh]);

  return {
    templates,
    wired,
    mailbox,
    loading,
    error,
    refresh,
    loadTemplate,
    saveTemplate,
    publishTemplate,
  };
}

export type { EmailTemplateDetail, EmailTemplateSummary, EmailMailboxStatus, EmailTemplateVariable };
